// Simple i18n for TimeFlash — English + Swedish.
// No external library; just a dict + a tiny hook with language stored in localStorage.

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const DICTIONARIES = {
  en: {
    "app.name": "TimeFlash",
    "app.tagline": "Scheduled Screen Flasher",
    "hero.badge": "Full-screen alerts // never miss a thing",
    "hero.line1": "Blast a message",
    "hero.line2": "on your screen",
    "hero.line3": "at the exact",
    "hero.line4": "moment you plan.",
    "hero.body": "Schedule as many flash alerts as you want. When the time hits, the whole screen flashes with your message until you stop it.",
    "header.enableAlerts": "Enable alerts",
    "header.alertsOn": "Alerts on",
    "header.pending": "pending",
    "header.shareApp": "Share TimeFlash",
    "header.signIn": "Sign in",
    "signIn.title": "Sign in to TimeFlash",
    "signIn.subtitle": "Flash reminders on your own screen. One account, all devices.",
    "footer.strip": "Keep this tab open · flashes run in your browser",
    "form.title": "New Flash Alert",
    "form.message": "Message to flash",
    "form.messagePlaceholder": "TAKE OUT THE TRASH",
    "form.datetime": "Date & time",
    "form.reminderOn": "Pre-flash reminder ON",
    "form.reminderOff": "Pre-flash reminder OFF",
    "form.reminderBefore": "Flash this far before",
    "form.customMinutes": "Or custom (minutes)",
    "form.repeat": "Repeat",
    "form.repeat.once": "Once",
    "form.repeat.daily": "Daily",
    "form.repeat.weekly": "Weekly",
    "form.display": "Display mode",
    "form.display.fit": "Fit screen",
    "form.display.fitHint": "Always visible",
    "form.display.scroll": "Scroll",
    "form.display.scrollHint": "Marquee across",
    "form.beepOn": "Beep alarm ON",
    "form.beepOff": "Beep alarm OFF",
    "form.submit": "Schedule Flash →",
    "form.submitting": "Scheduling...",
    "form.enabled": "[enabled]",
    "form.disabled": "[disabled]",
    "list.title": "Scheduled Flashes",
    "list.total": "total",
    "list.empty": "No flashes scheduled yet. Add one on the left.",
    "list.loading": "Loading schedules...",
    "list.chip.done": "Done",
    "list.chip.due": "Due",
    "list.chip.pending": "Pending",
    "list.chip.beep": "Beep",
    "list.chip.silent": "Silent",
    "list.chip.before": "before",
    "menu.google": "Google Calendar",
    "menu.ics": "Download .ics",
    "edit.title": "Edit Flash",
    "edit.save": "Save changes",
    "edit.saving": "Saving...",
    "share.title": "Share TimeFlash",
    "share.body": "Scan the QR with any phone camera or send the link below. The app installs with one tap — no account needed.",
    "share.copy": "Copy link",
    "share.share": "Share",
    "import.title": "Someone shared a flash",
    "import.add": "Add to my flashes",
    "import.adding": "Adding...",
    "flash.stop": "Stop",
    "flash.snooze": "Snooze",
    "flash.snoozeNoLeft": "No snooze left",
    "flash.snoozeHint": "Press any key · click · or Stop to end",
    "toast.scheduled": "FLASH SCHEDULED",
    "toast.updated": "SCHEDULE UPDATED",
    "toast.deleted": "SCHEDULE DELETED",
    "toast.imported": "FLASH IMPORTED",
    "toast.copied": "LINK COPIED",
    "toast.shared": "SHARED",
    "toast.snoozed": "SNOOZED",
    "toast.notifsOn": "NOTIFICATIONS ON",
    "toast.notifsBlocked": "NOTIFICATIONS BLOCKED",
    "toast.messageRequired": "MESSAGE REQUIRED",
    "toast.invalidDate": "INVALID DATE / TIME",
    "toast.invalidReminder": "INVALID REMINDER TIME",
    "toast.reminderPast": "REMINDER TIME ALREADY PASSED",
    "toast.failedSchedule": "FAILED TO SCHEDULE",
    "toast.failedLoad": "FAILED TO LOAD SCHEDULES",
    "toast.failedDelete": "DELETE FAILED",
    "toast.failedImport": "IMPORT FAILED",
    "toast.failedSnooze": "SNOOZE FAILED",
    "toast.failedSave": "SAVE FAILED",
    "list.testFlash": "Test",
    "list.testFlashTitle": "Preview this flash now",
    "toast.previewing": "TEST FLASH · TAP OR PRESS ANY KEY TO STOP",
    "tour.skip": "Skip",
    "tour.next": "Next",
    "tour.prev": "Back",
    "tour.done": "Got it",
    "tour.stepOf": "Step {n} of {total}",
    "tour.step1.title": "Welcome to TimeFlash",
    "tour.step1.body": "This app flashes your whole screen with a message at an exact time — until you stop it. Let's take a quick tour.",
    "tour.step2.title": "1. Write your message",
    "tour.step2.body": "Type what you want the screen to shout, pick a date and time, and (optionally) choose a ringtone, repeat and background colors.",
    "tour.step3.title": "2. Schedule it",
    "tour.step3.body": "Hit SCHEDULE FLASH. Your alert is saved to your account and shows up in the list on the right. Keep the tab open — TimeFlash takes care of the rest.",
    "tour.step4.title": "3. Test any flash",
    "tour.step4.body": "Not sure how it will look? Tap the yellow TEST button on any saved flash to preview the real full-screen effect and ringtone right now.",
    "tour.step5.title": "4. Stop whenever you want",
    "tour.step5.body": "When a flash fires, press any key, tap the screen, or hit STOP to end it. You can snooze, edit or share flashes any time. Enjoy!",
    "tpl.title": "Templates",
    "tpl.badge": "One-tap flashes",
    "tpl.empty": "No templates yet. Save any flash as a template to re-use it with one tap.",
    "tpl.saveBtn": "Save as template",
    "tpl.saveTitle": "Save this flash as a template",
    "tpl.nameLabel": "Template name",
    "tpl.namePlaceholder": "e.g. Take meds",
    "tpl.save": "Save template",
    "tpl.saving": "Saving...",
    "tpl.flashNow": "Flash now",
    "tpl.schedule": "Schedule",
    "tpl.delete": "Delete",
    "tpl.limitReached": "You've reached the limit of {max} templates. Delete one first.",
    "tpl.limitHint": "{count} of {max} used",
    "tpl.needMessage": "Fill in a message first — then save it as a template",
    "tpl.saved": "TEMPLATE SAVED",
    "tpl.deleted": "TEMPLATE DELETED",
    "tpl.failedSave": "COULD NOT SAVE TEMPLATE",
    "tpl.failedDelete": "COULD NOT DELETE TEMPLATE",
    "tplSched.title": "Schedule \"{name}\"",
    "tplSched.when": "Flash this at",
    "tplSched.cta": "Schedule flash",
    "tplSched.scheduling": "Scheduling...",
    "quiet.title": "Quiet hours",
    "quiet.badge": "Visual only · no sound",
    "quiet.hint": "During quiet hours flashes still appear on the screen — but the ringtone is muted. Reminders are muted too.",
    "quiet.toggleOn": "Quiet hours ON",
    "quiet.toggleOff": "Quiet hours OFF",
    "quiet.start": "From",
    "quiet.end": "Until",
    "quiet.wrapNote": "Window wraps across midnight if 'Until' is earlier than 'From'.",
    "quiet.save": "Save",
    "quiet.saving": "Saving...",
    "quiet.saved": "QUIET HOURS UPDATED",
    "quiet.failed": "COULD NOT SAVE QUIET HOURS",
    "quiet.openTitle": "Quiet hours",
    "quiet.activeChip": "QUIET",
    "quiet.statusActive": "Quiet now",
    "quiet.statusInactive": "Off",
    "cal.title": "Calendar sync",
    "cal.badge": "From your Google Calendar",
    "cal.openBtn": "Calendar",
    "cal.statusOn": "Synced",
    "cal.statusOff": "Off",
    "cal.urlLabel": "Secret calendar address (.ics)",
    "cal.urlPlaceholder": "https://calendar.google.com/calendar/ical/.../basic.ics",
    "cal.urlHint": "Google Calendar → Settings → your calendar → scroll to \"Secret address in iCal format\" → copy. We never show this address again after saving.",
    "cal.templateLabel": "Design to use for every calendar event",
    "cal.templateNone": "Default (no template)",
    "cal.toggleOn": "Sync enabled",
    "cal.toggleOff": "Sync disabled",
    "cal.save": "Save & Sync",
    "cal.saving": "Saving...",
    "cal.syncNow": "Sync now",
    "cal.syncing": "Syncing...",
    "cal.disconnect": "Disconnect",
    "cal.disconnected": "CALENDAR DISCONNECTED",
    "cal.saved": "CALENDAR SAVED",
    "cal.synced": "SYNCED {n} EVENT(S)",
    "cal.failed": "CALENDAR SYNC FAILED",
    "cal.lastSync": "Last sync",
    "cal.statusLabel": "Status",
    "cal.statusOk": "OK",
    "cal.lastCount": "{n} event(s) added",
    "cal.configured": "Calendar connected",
    "cal.notConfigured": "No calendar connected",
    "cal.titleTrim": "Event titles are trimmed to the first 15 characters.",
    "cal.needTemplate": "Pick a template first — then save",
  },
  sv: {
    "app.name": "TimeFlash",
    "app.tagline": "Schemalagd skärmblinkning",
    "hero.badge": "Fullskärmsvarningar // missa aldrig något",
    "hero.line1": "Blixtra ett meddelande",
    "hero.line2": "på din skärm",
    "hero.line3": "exakt när",
    "hero.line4": "du bestämmer.",
    "hero.body": "Schemalägg hur många blixtvarningar du vill. När tiden är inne blinkar hela skärmen med ditt meddelande tills du stoppar den.",
    "header.enableAlerts": "Aktivera aviseringar",
    "header.alertsOn": "Aviseringar på",
    "header.pending": "väntande",
    "header.shareApp": "Dela TimeFlash",
    "header.signIn": "Logga in",
    "signIn.title": "Logga in på TimeFlash",
    "signIn.subtitle": "Blixtra påminnelser på din egen skärm. Ett konto, alla enheter.",
    "footer.strip": "Håll fliken öppen · blixtarna körs i din webbläsare",
    "form.title": "Ny blixtvarning",
    "form.message": "Meddelande att blixtra",
    "form.messagePlaceholder": "TA UT SOPORNA",
    "form.datetime": "Datum och tid",
    "form.reminderOn": "Förvarning PÅ",
    "form.reminderOff": "Förvarning AV",
    "form.reminderBefore": "Blixtra så här långt innan",
    "form.customMinutes": "Eller eget (minuter)",
    "form.repeat": "Upprepa",
    "form.repeat.once": "En gång",
    "form.repeat.daily": "Dagligen",
    "form.repeat.weekly": "Varje vecka",
    "form.display": "Visningsläge",
    "form.display.fit": "Anpassa",
    "form.display.fitHint": "Alltid synlig",
    "form.display.scroll": "Rulla",
    "form.display.scrollHint": "Löper över skärmen",
    "form.beepOn": "Pipljud PÅ",
    "form.beepOff": "Pipljud AV",
    "form.submit": "Schemalägg blixt →",
    "form.submitting": "Schemalägger...",
    "form.enabled": "[aktiverad]",
    "form.disabled": "[inaktiverad]",
    "list.title": "Schemalagda blixtar",
    "list.total": "totalt",
    "list.empty": "Inga blixtar schemalagda än. Lägg till en till vänster.",
    "list.loading": "Laddar schema...",
    "list.chip.done": "Klar",
    "list.chip.due": "Nu",
    "list.chip.pending": "Väntar",
    "list.chip.beep": "Pip",
    "list.chip.silent": "Tyst",
    "list.chip.before": "innan",
    "menu.google": "Google Kalender",
    "menu.ics": "Ladda ner .ics",
    "edit.title": "Redigera blixt",
    "edit.save": "Spara ändringar",
    "edit.saving": "Sparar...",
    "share.title": "Dela TimeFlash",
    "share.body": "Skanna QR-koden med vilken telefonkamera som helst eller skicka länken nedan. Appen installeras med ett tryck — inget konto behövs.",
    "share.copy": "Kopiera länk",
    "share.share": "Dela",
    "import.title": "Någon har delat en blixt",
    "import.add": "Lägg till i mina blixtar",
    "import.adding": "Lägger till...",
    "flash.stop": "Stopp",
    "flash.snooze": "Snooza",
    "flash.snoozeNoLeft": "Ingen snooze kvar",
    "flash.snoozeHint": "Tryck på valfri tangent · klicka · eller Stopp för att avsluta",
    "toast.scheduled": "BLIXT SCHEMALAGD",
    "toast.updated": "SCHEMA UPPDATERAT",
    "toast.deleted": "SCHEMA RADERAT",
    "toast.imported": "BLIXT IMPORTERAD",
    "toast.copied": "LÄNK KOPIERAD",
    "toast.shared": "DELAT",
    "toast.snoozed": "SNOOZAD",
    "toast.notifsOn": "AVISERINGAR PÅ",
    "toast.notifsBlocked": "AVISERINGAR BLOCKERADE",
    "toast.messageRequired": "MEDDELANDE KRÄVS",
    "toast.invalidDate": "OGILTIGT DATUM / TID",
    "toast.invalidReminder": "OGILTIG PÅMINNELSETID",
    "toast.reminderPast": "PÅMINNELSETID HAR PASSERATS",
    "toast.failedSchedule": "KUNDE INTE SCHEMALÄGGA",
    "toast.failedLoad": "KUNDE INTE LADDA",
    "toast.failedDelete": "RADERING MISSLYCKADES",
    "toast.failedImport": "IMPORT MISSLYCKADES",
    "toast.failedSnooze": "SNOOZE MISSLYCKADES",
    "toast.failedSave": "SPARA MISSLYCKADES",
    "list.testFlash": "Testa",
    "list.testFlashTitle": "Förhandsgranska denna blixt nu",
    "toast.previewing": "TESTBLIXT · TRYCK VAR SOM HELST FÖR ATT STOPPA",
    "tour.skip": "Hoppa över",
    "tour.next": "Nästa",
    "tour.prev": "Tillbaka",
    "tour.done": "Jag förstår",
    "tour.stepOf": "Steg {n} av {total}",
    "tour.step1.title": "Välkommen till TimeFlash",
    "tour.step1.body": "Den här appen blinkar hela skärmen med ett meddelande vid en exakt tidpunkt — tills du stoppar den. Nu tar vi en snabb rundtur.",
    "tour.step2.title": "1. Skriv ditt meddelande",
    "tour.step2.body": "Skriv vad du vill att skärmen ska skrika ut, välj datum och tid, och (om du vill) ringsignal, upprepning och bakgrundsfärger.",
    "tour.step3.title": "2. Schemalägg den",
    "tour.step3.body": "Tryck på SCHEMALÄGG BLIXT. Din varning sparas på ditt konto och visas i listan till höger. Håll fliken öppen — TimeFlash sköter resten.",
    "tour.step4.title": "3. Testa vilken blixt som helst",
    "tour.step4.body": "Osäker på hur den kommer se ut? Tryck på den gula TESTA-knappen på vilken sparad blixt som helst för att förhandsgranska den riktiga fullskärmseffekten och ringsignalen direkt.",
    "tour.step5.title": "4. Stoppa när du vill",
    "tour.step5.body": "När en blixt startar, tryck på valfri tangent, klicka på skärmen eller tryck på STOPP för att avsluta. Du kan snooza, redigera eller dela blixtar när som helst. Lycka till!",
    "tpl.title": "Mallar",
    "tpl.badge": "Blixtar med ett tryck",
    "tpl.empty": "Inga mallar än. Spara valfri blixt som mall för att återanvända den med ett tryck.",
    "tpl.saveBtn": "Spara som mall",
    "tpl.saveTitle": "Spara denna blixt som en mall",
    "tpl.nameLabel": "Mallens namn",
    "tpl.namePlaceholder": "t.ex. Ta medicin",
    "tpl.save": "Spara mall",
    "tpl.saving": "Sparar...",
    "tpl.flashNow": "Blixtra nu",
    "tpl.schedule": "Schemalägg",
    "tpl.delete": "Radera",
    "tpl.limitReached": "Du har nått gränsen på {max} mallar. Radera en först.",
    "tpl.limitHint": "{count} av {max} använda",
    "tpl.needMessage": "Fyll i ett meddelande först — spara det sedan som mall",
    "tpl.saved": "MALL SPARAD",
    "tpl.deleted": "MALL RADERAD",
    "tpl.failedSave": "KUNDE INTE SPARA MALL",
    "tpl.failedDelete": "KUNDE INTE RADERA MALL",
    "tplSched.title": "Schemalägg \"{name}\"",
    "tplSched.when": "Blixtra detta klockan",
    "tplSched.cta": "Schemalägg blixt",
    "tplSched.scheduling": "Schemalägger...",
    "quiet.title": "Tysta timmar",
    "quiet.badge": "Bara visuellt · inget ljud",
    "quiet.hint": "Under tysta timmar visas blixtar fortfarande på skärmen — men ringsignalen är tystad. Påminnelser är också tystade.",
    "quiet.toggleOn": "Tysta timmar PÅ",
    "quiet.toggleOff": "Tysta timmar AV",
    "quiet.start": "Från",
    "quiet.end": "Till",
    "quiet.wrapNote": "Perioden går över midnatt om 'Till' är tidigare än 'Från'.",
    "quiet.save": "Spara",
    "quiet.saving": "Sparar...",
    "quiet.saved": "TYSTA TIMMAR UPPDATERADE",
    "quiet.failed": "KUNDE INTE SPARA",
    "quiet.openTitle": "Tysta timmar",
    "quiet.activeChip": "TYST",
    "quiet.statusActive": "Tyst nu",
    "quiet.statusInactive": "Av",
    "cal.title": "Kalendersynk",
    "cal.badge": "Från din Google-kalender",
    "cal.openBtn": "Kalender",
    "cal.statusOn": "Synkad",
    "cal.statusOff": "Av",
    "cal.urlLabel": "Hemlig kalenderadress (.ics)",
    "cal.urlPlaceholder": "https://calendar.google.com/calendar/ical/.../basic.ics",
    "cal.urlHint": "Google Kalender → Inställningar → din kalender → rulla till \"Hemlig adress i iCal-format\" → kopiera. Vi visar aldrig adressen igen efter att du sparat.",
    "cal.templateLabel": "Design som används för varje kalenderhändelse",
    "cal.templateNone": "Standard (ingen mall)",
    "cal.toggleOn": "Synk påslagen",
    "cal.toggleOff": "Synk avstängd",
    "cal.save": "Spara & Synka",
    "cal.saving": "Sparar...",
    "cal.syncNow": "Synka nu",
    "cal.syncing": "Synkar...",
    "cal.disconnect": "Koppla från",
    "cal.disconnected": "KALENDER FRÅNKOPPLAD",
    "cal.saved": "KALENDER SPARAD",
    "cal.synced": "{n} HÄNDELSE(R) SYNKADE",
    "cal.failed": "KALENDERSYNK MISSLYCKADES",
    "cal.lastSync": "Senaste synk",
    "cal.statusLabel": "Status",
    "cal.statusOk": "OK",
    "cal.lastCount": "{n} händelse(r) tillagda",
    "cal.configured": "Kalender ansluten",
    "cal.notConfigured": "Ingen kalender ansluten",
    "cal.titleTrim": "Händelsetitlar kortas till de 15 första tecknen.",
    "cal.needTemplate": "Välj en mall först — spara sedan",
  },
};

const detectDefault = () => {
  try {
    const saved = localStorage.getItem("tf_lang");
    if (saved && DICTIONARIES[saved]) return saved;
    const nav = (navigator.language || "en").toLowerCase();
    if (nav.startsWith("sv")) return "sv";
  } catch {}
  return "en";
};

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectDefault);
  const setLang = useCallback((l) => {
    if (!DICTIONARIES[l]) return;
    try {
      localStorage.setItem("tf_lang", l);
    } catch {}
    setLangState(l);
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const t = useCallback(
    (key, fallback) => DICTIONARIES[lang]?.[key] ?? fallback ?? key,
    [lang]
  );
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);

export const LANGUAGES = [
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "sv", label: "SV", flag: "🇸🇪" },
];
